import { Injectable } from '@angular/core';
import { Project, PiledModule, PullTestingModule, stripProjectModuleEmbeds } from 'shared_models';

/** Sibling module payloads mirrored from {@link ProjectFacadeService} after a successful load. */
export interface ProjectModulesContextPayload {
  project: Project;
  piledModule: PiledModule | null;
  pullTestingModule: PullTestingModule | null;
}

export interface ProjectContextSnapshot {
  projectID: string;
  projectName: string;
  project?: Project;
  piledModule?: PiledModule | null;
  pullTestingModule?: PullTestingModule | null;
  updatedAt: number;
}

@Injectable({
  providedIn: 'root',
})
/**
 * Application context store for verified in-memory objects.
 * This service is intentionally state-only and should not perform API calls.
 * Full page reload clears this store. Prefer `ProjectFacadeService.loadProjectModules` /
 * `ensureLoaded` from routed features; they populate this store after fetching from the API.
 */
export class AppContextService {
  private projectContexts = new Map<string, ProjectContextSnapshot>();
  private activeProjectID: string | null = null;

  /**
   * Stores high-level `project` (without embedded `piled` / `pullTesting`) plus module siblings.
   */
  setProjectModulesContext(payload: ProjectModulesContextPayload): void {
    const { project, piledModule, pullTestingModule } = payload;
    if (!project?.projectID || !project?.name) return;
    const snapshot: ProjectContextSnapshot = {
      projectID: project.projectID,
      projectName: project.name,
      project: stripProjectModuleEmbeds(project),
      piledModule,
      pullTestingModule,
      updatedAt: Date.now(),
    };
    this.projectContexts.set(project.projectID, snapshot);
    this.activeProjectID = project.projectID;
  }

  getProjectContext(projectID?: string | null): ProjectContextSnapshot | null {
    const key = projectID ?? this.activeProjectID;
    if (!key) return null;
    return this.projectContexts.get(key) ?? null;
  }

  getProjectName(projectID?: string | null): string | null {
    return this.getProjectContext(projectID)?.projectName ?? null;
  }

  invalidateProject(projectID: string): void {
    this.projectContexts.delete(projectID);
    if (this.activeProjectID === projectID) {
      this.activeProjectID = null;
    }
  }

  clearAll(): void {
    this.projectContexts.clear();
    this.activeProjectID = null;
  }
}
